import React from 'react';
import { motion } from 'framer-motion';
import { SearchIcon, CalendarIcon, FileTextIcon, FolderIcon, DollarSignIcon, ClipboardListIcon, AlertTriangleIcon, MessageSquareIcon, BarChartIcon } from 'lucide-react';
const cycleItems = [{
  icon: SearchIcon,
  label: 'Buscar datos de clientes'
}, {
  icon: FolderIcon,
  label: 'Revisar carpetas para encontrar documentos'
}, {
  icon: CalendarIcon,
  label: 'Controlar vencimientos fiscales'
}, {
  icon: FileTextIcon,
  label: 'Pedir nuevamente documentación que ya habías solicitado'
}, {
  icon: BarChartIcon,
  label: 'Revisar planillas para ver qué tareas están pendientes'
}, {
  icon: MessageSquareIcon,
  label: 'Responder consultas de clientes'
}, {
  icon: DollarSignIcon,
  label: 'Controlar pagos o servicios pendientes'
}];
const realProblemItems = ['Gestionar múltiples clientes.', 'Controlar vencimientos fiscales.', 'Registrar avances contables.', 'Organizar documentación.', 'Responder consultas.', 'Preparar cierres.', 'Controlar pagos.'];
const resultItems = ['Horas perdidas buscando información.', 'Tareas repetidas todos los meses.', 'Errores evitables por desorganización.', 'Estrés cada vez que se acercan fechas de vencimiento.'];
export function ProblemSection() {
  return <section className="py-20 px-4 bg-[#0B1A2E] border-t border-slate-800" data-id="element-326">
      <div className="max-w-3xl mx-auto" data-id="element-327">
        {/* 1. EMOTIONAL IDENTIFICATION */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-14" data-id="element-328">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-8 leading-tight" data-id="element-329">
            Si sos contador independiente, probablemente esto te resulte
            demasiado familiar…
          </h2>

          <div className="bg-slate-800/40 border border-slate-700/50 rounded-2xl p-6 md:p-8 text-left" data-id="element-330">
            <div className="space-y-4 text-base md:text-lg text-slate-300 leading-relaxed" data-id="element-331">
              {['Llegás al estudio.', 'Abrís tu computadora.', 'Sabés que tenés clientes esperando respuestas, vencimientos que se acercan y tareas pendientes.', 'Pero antes de empezar a trabajar… tenés que empezar a buscar información.'].map((line, i) => <motion.p key={i} initial={{
              opacity: 0,
              x: -10
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: i * 0.12
            }} data-id="element-332">
                  {line}
                </motion.p>)}

              <div className="border-l-2 border-red-500/40 pl-4 space-y-2 my-2" data-id="element-333">
                {['Archivos en distintas carpetas.', 'Planillas en Excel.', 'Mensajes de clientes en WhatsApp.', 'Correos con documentos adjuntos.', 'Notas con recordatorios.'].map((line, i) => <motion.p key={i} initial={{
                opacity: 0,
                x: -10
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: 0.48 + i * 0.12
              }} className="text-slate-400" data-id="element-334">
                    {line}
                  </motion.p>)}
              </div>

              <motion.p initial={{
              opacity: 0
            }} whileInView={{
              opacity: 1
            }} viewport={{
              once: true
            }} transition={{
              delay: 1.08
            }} className="text-white font-medium pt-2" data-id="element-335">
                Y de repente te encontrás haciendo algo que no debería ocupar
                gran parte de tu día:
              </motion.p>
              <motion.p initial={{
              opacity: 0
            }} whileInView={{
              opacity: 1
            }} viewport={{
              once: true
            }} transition={{
              delay: 1.2
            }} className="text-white font-bold" data-id="element-336">
                organizar información que debería estar organizada desde el
                principio.
              </motion.p>
            </div>
          </div>
        </motion.div>

        {/* 2. DAILY CYCLE */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="mb-14" data-id="element-337">
          <div className="flex items-center justify-center gap-3 mb-8" data-id="element-338">
            <div className="h-px flex-1 bg-gradient-to-r from-transparent to-red-500/30" data-id="element-339" />
            <h3 className="text-lg md:text-xl font-bold text-red-400 uppercase tracking-wider text-center whitespace-nowrap" data-id="element-340">
              🔄 El día a día de muchos contadores independientes
            </h3>
            <div className="h-px flex-1 bg-gradient-to-l from-transparent to-red-500/30" data-id="element-341" />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4 mb-6" data-id="element-342">
            {cycleItems.map((item, index) => {
            const Icon = item.icon;
            return <motion.div key={index} initial={{
              opacity: 0,
              scale: 0.9
            }} whileInView={{
              opacity: 1,
              scale: 1
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.08
            }} className="bg-red-950/20 border border-red-900/30 rounded-xl p-4 flex flex-col items-center text-center gap-3" data-id="element-343">
                  <div className="bg-red-500/10 p-2.5 rounded-full" data-id="element-344">
                    <Icon className="w-5 h-5 text-red-400" data-id="element-345" />
                  </div>
                  <span className="text-sm text-slate-300 font-medium leading-tight" data-id="element-346">
                    {item.label}
                  </span>
                </motion.div>;
          })}
          </div>

          <motion.div initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} className="text-center space-y-1 text-slate-400 text-base md:text-lg" data-id="element-347">
            <p data-id="element-348">Y mientras tanto…</p>
            <p data-id="element-349">los vencimientos siguen avanzando.</p>
            <p data-id="element-350">Los clientes siguen consultando.</p>
            <p className="text-white font-medium" data-id="element-351">
              Y el trabajo sigue acumulándose.
            </p>
          </motion.div>
        </motion.div>

        {/* 3. THE RESULT */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="mb-14" data-id="element-352">
          <div className="flex items-center justify-center gap-3 mb-8" data-id="element-353">
            <div className="h-px flex-1 bg-gradient-to-r from-transparent to-red-500/30" data-id="element-354" />
            <h3 className="text-lg md:text-xl font-bold text-red-400 uppercase tracking-wider text-center whitespace-nowrap" data-id="element-355">
              El resultado
            </h3>
            <div className="h-px flex-1 bg-gradient-to-l from-transparent to-red-500/30" data-id="element-356" />
          </div>

          <div className="bg-slate-800/40 border border-slate-700/50 rounded-2xl p-6 md:p-8" data-id="element-357">
            <div className="space-y-3 mb-8" data-id="element-358">
              {resultItems.map((item, index) => <motion.div key={index} initial={{
              opacity: 0,
              x: -15
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.08
            }} className="flex items-center gap-3" data-id="element-359">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0" data-id="element-360" />
                  <span className="text-base md:text-lg text-slate-200" data-id="element-361">
                    {item}
                  </span>
                </motion.div>)}
            </div>

            <div className="border-l-2 border-blue-500/40 pl-5 space-y-2" data-id="element-362">
              <p className="text-slate-400 text-base" data-id="element-363">
                No porque no sepas hacer tu trabajo.
              </p>
              <p className="text-slate-400 text-base" data-id="element-364">
                No porque no seas un buen profesional.
              </p>
              <p className="text-white text-base md:text-lg font-semibold" data-id="element-365">
                Sino porque estás intentando gestionar tu estudio contable sin
                un sistema claro que te ayude a organizarlo.
              </p>
            </div>
          </div>
        </motion.div>

        {/* 4. THE REAL PROBLEM */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="mb-14" data-id="element-366">
          <div className="flex items-center justify-center gap-3 mb-8" data-id="element-367">
            <div className="h-px flex-1 bg-gradient-to-r from-transparent to-amber-500/30" data-id="element-368" />
            <h3 className="text-lg md:text-xl font-bold text-amber-400 uppercase tracking-wider text-center whitespace-nowrap flex items-center gap-2" data-id="element-369">
              <AlertTriangleIcon className="w-5 h-5" data-id="element-370" />
              El problema real
            </h3>
            <div className="h-px flex-1 bg-gradient-to-l from-transparent to-amber-500/30" data-id="element-371" />
          </div>

          <div className="bg-slate-800/40 border border-slate-700/50 rounded-2xl p-6 md:p-8" data-id="element-372">
            <p className="text-slate-300 text-base md:text-lg mb-5" data-id="element-373">
              Como contador independiente tenés que hacer muchas cosas al mismo
              tiempo:
            </p>

            <div className="space-y-3 mb-8" data-id="element-374">
              {realProblemItems.map((item, index) => <motion.div key={index} initial={{
              opacity: 0,
              x: -15
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.08
            }} className="flex items-center gap-3" data-id="element-375">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0" data-id="element-376" />
                  <span className="text-base md:text-lg text-slate-200" data-id="element-377">
                    {item}
                  </span>
                </motion.div>)}
            </div>

            <p className="text-slate-300 text-base md:text-lg mb-6 font-medium" data-id="element-378">
              Y todo esto mientras tratás de mantener orden en tu estudio.
            </p>

            <div className="border-l-2 border-blue-500/40 pl-5 space-y-2" data-id="element-379">
              <p className="text-slate-400 text-base" data-id="element-380">
                El problema no es la cantidad de trabajo.
              </p>
              <p className="text-white text-base md:text-lg font-semibold" data-id="element-381">
                El problema es intentar gestionar todo sin un sistema que
                centralice y ordene la información.
              </p>
            </div>
          </div>
        </motion.div>

        {/* 5. EMOTIONAL CLOSING */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center bg-slate-800/30 border border-slate-700/50 rounded-2xl p-8 md:p-10" data-id="element-382">
          <p className="text-xl md:text-2xl text-slate-300 leading-relaxed mb-3" data-id="element-383">
            El problema no es la cantidad de trabajo.
          </p>
          <p className="text-xl md:text-2xl text-white font-bold leading-relaxed" data-id="element-384">
            El problema es intentar gestionar todo sin un sistema que centralice
            y ordene la información.
          </p>
        </motion.div>
      </div>
    </section>;
}